import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { buildChatRoomTopic, buildChatTypingDestination, buildChatTypingPayload, buildChatTypingTopic, chatMessageDtoToInbound, mergeChatTimelineEntries, newestChatMessageDto, parseChatLiveEvent, parseChatTypingEvent, sortChatMessageDtos, } from '@/shared/model/chat.model';
import { buildChatUserTopic, parseChatUserRoomEvent } from '@/shared/model/chat-room.model';
import { acquireCrmStompSharedConnection, getCrmStompSharedClient, onCrmStompSharedConnect, onCrmStompSharedConnectionState, releaseCrmStompSharedConnection, } from '@/shared/ws/crmStompSharedClient';
import { fetchChatMessages, markChatRoomRead, sendChatMessage, } from '@/services/chat/chatApi';
function chatUiReducer(state, action) {
    switch (action.type) {
        case 'SET_HISTORY': {
            const prev = state.entriesByRoom[action.roomId] ?? [];
            return {
                ...state,
                entriesByRoom: {
                    ...state.entriesByRoom,
                    [action.roomId]: mergeChatTimelineEntries(prev, action.entries),
                },
            };
        }
        case 'ADD_MESSAGE': {
            const prev = state.entriesByRoom[action.roomId] ?? [];
            if (action.message.id != null && prev.some((e) => e.type === 'message' && e.message.id === action.message.id)) {
                return state;
            }
            return {
                ...state,
                entriesByRoom: {
                    ...state.entriesByRoom,
                    [action.roomId]: [...prev, { type: 'message', message: action.message }],
                },
            };
        }
        case 'UPDATE_MESSAGE': {
            const prev = state.entriesByRoom[action.roomId] ?? [];
            return {
                ...state,
                entriesByRoom: {
                    ...state.entriesByRoom,
                    [action.roomId]: prev.map((e) => e.type === 'message' && e.message.id === action.message.id
                        ? { type: 'message', message: action.message }
                        : e),
                },
            };
        }
        case 'REMOVE_MESSAGE': {
            const prev = state.entriesByRoom[action.roomId] ?? [];
            return {
                ...state,
                entriesByRoom: {
                    ...state.entriesByRoom,
                    [action.roomId]: prev.filter((e) => !(e.type === 'message' && e.message.id === action.messageId)),
                },
            };
        }
        case 'ADD_SYSTEM': {
            const prev = state.entriesByRoom[action.roomId] ?? [];
            return {
                ...state,
                entriesByRoom: {
                    ...state.entriesByRoom,
                    [action.roomId]: [...prev, { type: 'system', text: action.text }],
                },
            };
        }
        case 'CLEAR_UNREAD':
            return {
                ...state,
                unreadByRoom: { ...state.unreadByRoom, [action.roomId]: 0 },
            };
        case 'INCR_UNREAD': {
            const n = state.unreadByRoom[action.roomId] ?? 0;
            return {
                ...state,
                unreadByRoom: { ...state.unreadByRoom, [action.roomId]: n + 1 },
            };
        }
        default:
            return state;
    }
}
function historyFromDtos(dtos) {
    const entries = [];
    for (const dto of sortChatMessageDtos(dtos)) {
        const msg = chatMessageDtoToInbound(dto);
        if (msg) {
            entries.push({ type: 'message', message: msg });
        }
    }
    return entries;
}
export function useCrmChat(options) {
    const { username, isChatVisible = true, enabled = true, sockJsUrl = '/websocket/app', onForeignRoomMessage, onLiveMessage, onForeignRoomActivity, onMemberAdded, debug = false, } = options;
    const [roomIds, setRoomIds] = useState([]);
    const roomIdsRef = useRef([]);
    const [currentRoomId, setCurrentRoomId] = useState(null);
    const [connectionState, setConnectionState] = useState(enabled ? 'connecting' : 'idle');
    const [ui, dispatch] = useReducer(chatUiReducer, {
        entriesByRoom: {},
        unreadByRoom: {},
    });
    const [typingUsers, setTypingUsers] = useState([]);
    const clientRef = useRef(null);
    const subscriptionsRef = useRef(new Map());
    const typingSubscriptionsRef = useRef(new Map());
    const userTopicSubscriptionRef = useRef(null);
    const roomMetaRef = useRef(new Map());
    const currentRoomIdRef = useRef(null);
    const usernameRef = useRef(username);
    const onForeignRoomMessageRef = useRef(onForeignRoomMessage);
    const onLiveMessageRef = useRef(onLiveMessage);
    const onForeignRoomActivityRef = useRef(onForeignRoomActivity);
    const onMemberAddedRef = useRef(onMemberAdded);
    const isChatVisibleRef = useRef(isChatVisible);
    const markReadTimerRef = useRef(null);
    const typingStopTimerRef = useRef(null);
    const typingOutboundActiveRef = useRef(false);
    const typingInboundExpiryRef = useRef(new Map());
    const historyLoadedRef = useRef(new Set());
    const needsHistoryCatchUpRef = useRef(false);
    const knownRoomIdsRef = useRef(new Set());
    const visibleActiveRoomId = useCallback(() => {
        if (!isChatVisibleRef.current)
            return null;
        return currentRoomIdRef.current;
    }, []);
    const visibleActiveRoomIdRef = useRef(visibleActiveRoomId);
    visibleActiveRoomIdRef.current = visibleActiveRoomId;
    useEffect(() => {
        usernameRef.current = username;
    }, [username]);
    useEffect(() => {
        onForeignRoomMessageRef.current = onForeignRoomMessage;
    }, [onForeignRoomMessage]);
    useEffect(() => {
        onLiveMessageRef.current = onLiveMessage;
    }, [onLiveMessage]);
    useEffect(() => {
        onForeignRoomActivityRef.current = onForeignRoomActivity;
    }, [onForeignRoomActivity]);
    useEffect(() => {
        onMemberAddedRef.current = onMemberAdded;
    }, [onMemberAdded]);
    useEffect(() => {
        isChatVisibleRef.current = isChatVisible;
    }, [isChatVisible]);
    const tearDownSubscriptions = useCallback(() => {
        if (userTopicSubscriptionRef.current) {
            userTopicSubscriptionRef.current.unsubscribe();
            userTopicSubscriptionRef.current = null;
        }
        for (const sub of subscriptionsRef.current.values()) {
            sub.unsubscribe();
        }
        subscriptionsRef.current.clear();
        for (const sub of typingSubscriptionsRef.current.values()) {
            sub.unsubscribe();
        }
        typingSubscriptionsRef.current.clear();
        setTypingUsers([]);
    }, []);
    const scheduleMarkActiveRoomRead = useCallback((roomId, messageId) => {
        if (visibleActiveRoomIdRef.current() !== roomId)
            return;
        if (markReadTimerRef.current != null) {
            window.clearTimeout(markReadTimerRef.current);
        }
        markReadTimerRef.current = window.setTimeout(() => {
            markReadTimerRef.current = null;
            void markChatRoomRead(roomId, messageId).catch(() => undefined);
        }, 400);
    }, []);
    const handleLiveEvent = useCallback((roomId, raw) => {
        const event = parseChatLiveEvent(raw);
        if (!event) {
            if (debug) {
                console.debug('[CRM Chat] ignored STOMP frame', roomId, raw.slice(0, 200));
            }
            return;
        }
        const { type, message } = event;
        const active = visibleActiveRoomIdRef.current();
        const me = usernameRef.current;
        const isOwn = message.fromUser === me;
        if (type === 'CREATED') {
            dispatch({ type: 'ADD_MESSAGE', roomId, message });
            const incrementUnread = !isOwn && active !== roomId;
            onLiveMessageRef.current?.({ roomId, message, incrementUnread });
        }
        else if (type === 'UPDATED') {
            if (message.deleted) {
                if (message.id != null) {
                    dispatch({ type: 'REMOVE_MESSAGE', roomId, messageId: message.id });
                }
            }
            else {
                dispatch({ type: 'UPDATE_MESSAGE', roomId, message });
            }
        }
        else if (type === 'DELETED') {
            if (message.id != null) {
                dispatch({ type: 'REMOVE_MESSAGE', roomId, messageId: message.id });
            }
        }
        if (type === 'CREATED' && active !== roomId && !isOwn) {
            dispatch({ type: 'INCR_UNREAD', roomId });
        }
        if (type === 'CREATED' && active === roomId && message.id != null) {
            scheduleMarkActiveRoomRead(roomId, message.id);
        }
        if (!isOwn && active !== roomId && type === 'CREATED') {
            const meta = roomMetaRef.current.get(roomId);
            const roomLabel = meta?.name?.trim() || meta?.roomKey || `room ${roomId}`;
            const preview = message.content.length > 120 ? `${message.content.slice(0, 117)}…` : message.content;
            onForeignRoomMessageRef.current?.({
                fromUser: message.fromUser,
                roomId,
                roomLabel,
                preview,
            });
            onForeignRoomActivityRef.current?.(roomId);
        }
    }, [debug]);
    const handleLiveEventRef = useRef(handleLiveEvent);
    handleLiveEventRef.current = handleLiveEvent;
    const subscribeRoom = useCallback((roomId) => {
        const client = clientRef.current;
        if (!client?.connected)
            return;
        if (!subscriptionsRef.current.has(roomId)) {
            const sub = client.subscribe(buildChatRoomTopic(roomId), (frame) => {
                handleLiveEventRef.current(roomId, frame.body);
            });
            subscriptionsRef.current.set(roomId, sub);
            if (debug) {
                console.debug('[CRM Chat] subscribed', buildChatRoomTopic(roomId));
            }
        }
        if (!typingSubscriptionsRef.current.has(roomId)) {
            const typingSub = client.subscribe(buildChatTypingTopic(roomId), (frame) => {
                const parsed = parseChatTypingEvent(frame.body);
                if (!parsed)
                    return;
                const userId = parsed.userId?.trim();
                if (!userId || userId === usernameRef.current)
                    return;
                if (visibleActiveRoomIdRef.current() !== roomId)
                    return;
                const clearInboundExpiry = (id) => {
                    const timerId = typingInboundExpiryRef.current.get(id);
                    if (timerId != null) {
                        window.clearTimeout(timerId);
                        typingInboundExpiryRef.current.delete(id);
                    }
                };
                if (parsed.typing) {
                    clearInboundExpiry(userId);
                    const expiryId = window.setTimeout(() => {
                        typingInboundExpiryRef.current.delete(userId);
                        setTypingUsers((prev) => prev.filter((id) => id !== userId));
                    }, 5000);
                    typingInboundExpiryRef.current.set(userId, expiryId);
                    setTypingUsers((prev) => (prev.includes(userId) ? prev : [...prev, userId]));
                }
                else {
                    clearInboundExpiry(userId);
                    setTypingUsers((prev) => prev.filter((id) => id !== userId));
                }
            });
            typingSubscriptionsRef.current.set(roomId, typingSub);
        }
    }, [debug]);
    const subscribeRoomRef = useRef(subscribeRoom);
    subscribeRoomRef.current = subscribeRoom;
    const subscribeUserTopic = useCallback(() => {
        const client = clientRef.current;
        if (!client?.connected)
            return;
        const me = usernameRef.current?.trim();
        if (!me)
            return;
        let topic;
        try {
            topic = buildChatUserTopic(me);
        }
        catch {
            return;
        }
        if (userTopicSubscriptionRef.current) {
            userTopicSubscriptionRef.current.unsubscribe();
            userTopicSubscriptionRef.current = null;
        }
        userTopicSubscriptionRef.current = client.subscribe(topic, (frame) => {
            const event = parseChatUserRoomEvent(frame.body);
            if (!event || event.eventType !== 'MEMBER_ADDED')
                return;
            subscribeRoomRef.current(event.roomId);
            onMemberAddedRef.current?.(event);
            const isActive = currentRoomIdRef.current === event.roomId;
            if (!historyLoadedRef.current.has(event.roomId)) {
                void loadRoomHistoryRef.current(event.roomId, { markRead: isActive });
            }
        });
        if (debug) {
            console.debug('[CRM Chat] subscribed', topic);
        }
    }, [debug]);
    const subscribeUserTopicRef = useRef(subscribeUserTopic);
    subscribeUserTopicRef.current = subscribeUserTopic;
    const syncTopicSubscriptions = useCallback((ids) => {
        const client = clientRef.current;
        if (!client?.connected)
            return;
        const next = new Set(ids);
        for (const [id, sub] of subscriptionsRef.current) {
            if (!next.has(id)) {
                sub.unsubscribe();
                subscriptionsRef.current.delete(id);
            }
        }
        for (const [id, sub] of typingSubscriptionsRef.current) {
            if (!next.has(id)) {
                sub.unsubscribe();
                typingSubscriptionsRef.current.delete(id);
            }
        }
        for (const id of ids) {
            subscribeRoomRef.current(id);
        }
    }, []);
    const syncTopicSubscriptionsRef = useRef(syncTopicSubscriptions);
    syncTopicSubscriptionsRef.current = syncTopicSubscriptions;
    /** One-time REST history per room (or after reconnect). Live updates come from STOMP only. */
    const loadRoomHistory = useCallback(async (roomId, options) => {
        const markRead = options?.markRead !== false;
        try {
            const dtos = await fetchChatMessages(roomId);
            dispatch({ type: 'SET_HISTORY', roomId, entries: historyFromDtos(dtos) });
            historyLoadedRef.current.add(roomId);
            const last = newestChatMessageDto(dtos);
            if (markRead && last?.id != null) {
                await markChatRoomRead(roomId, last.id);
            }
        }
        catch (e) {
            console.error('[CRM Chat] failed to load history', roomId, e);
        }
    }, []);
    const loadRoomHistoryRef = useRef(loadRoomHistory);
    loadRoomHistoryRef.current = loadRoomHistory;
    const appendSystemLine = useCallback((roomId, text) => {
        dispatch({ type: 'ADD_SYSTEM', roomId, text });
    }, []);
    const selectRoom = useCallback((roomId) => {
        if (currentRoomIdRef.current === roomId)
            return;
        currentRoomIdRef.current = roomId;
        setCurrentRoomId(roomId);
        setTypingUsers([]);
        for (const timerId of typingInboundExpiryRef.current.values()) {
            window.clearTimeout(timerId);
        }
        typingInboundExpiryRef.current.clear();
        if (typingStopTimerRef.current != null) {
            window.clearTimeout(typingStopTimerRef.current);
            typingStopTimerRef.current = null;
        }
        typingOutboundActiveRef.current = false;
        dispatch({ type: 'CLEAR_UNREAD', roomId });
        if (!historyLoadedRef.current.has(roomId)) {
            void loadRoomHistoryRef.current(roomId);
        }
        subscribeRoomRef.current(roomId);
    }, []);
    const syncRooms = useCallback((rooms) => {
        const activeRooms = rooms.filter((r) => r.id != null && !r.archived);
        const meta = new Map();
        const ids = [];
        for (const room of activeRooms) {
            meta.set(room.id, room);
            ids.push(room.id);
        }
        const prevKnown = knownRoomIdsRef.current;
        const newlyDiscovered = ids.filter((id) => !prevKnown.has(id));
        knownRoomIdsRef.current = new Set(ids);
        roomMetaRef.current = meta;
        roomIdsRef.current = ids;
        setRoomIds(ids);
        syncTopicSubscriptionsRef.current(ids);
        for (const id of newlyDiscovered) {
            const isActive = currentRoomIdRef.current === id;
            if (isActive && !historyLoadedRef.current.has(id)) {
                void loadRoomHistoryRef.current(id, { markRead: isActive });
            }
        }
    }, []);
    const publishTyping = useCallback((roomId, typing) => {
        const client = clientRef.current;
        if (!client?.connected)
            return;
        client.publish({
            destination: buildChatTypingDestination(roomId),
            body: buildChatTypingPayload(usernameRef.current, typing),
        });
        typingOutboundActiveRef.current = typing;
    }, []);
    const notifyTyping = useCallback((typing) => {
        const roomId = currentRoomIdRef.current;
        if (roomId == null)
            return;
        if (typing) {
            if (!typingOutboundActiveRef.current) {
                publishTyping(roomId, true);
            }
            if (typingStopTimerRef.current != null) {
                window.clearTimeout(typingStopTimerRef.current);
            }
            typingStopTimerRef.current = window.setTimeout(() => {
                typingStopTimerRef.current = null;
                if (typingOutboundActiveRef.current) {
                    publishTyping(roomId, false);
                }
            }, 2500);
            return;
        }
        if (typingStopTimerRef.current != null) {
            window.clearTimeout(typingStopTimerRef.current);
            typingStopTimerRef.current = null;
        }
        if (typingOutboundActiveRef.current) {
            publishTyping(roomId, false);
        }
    }, [publishTyping]);
    const sendMessage = useCallback(async (content) => {
        const trimmed = content.trim();
        const roomId = currentRoomIdRef.current;
        if (!trimmed || roomId == null)
            return;
        notifyTyping(false);
        try {
            const saved = await sendChatMessage(roomId, trimmed);
            const inbound = chatMessageDtoToInbound(saved);
            if (inbound) {
                dispatch({ type: 'ADD_MESSAGE', roomId, message: inbound });
                onLiveMessageRef.current?.({ roomId, message: inbound, incrementUnread: false });
            }
            if (saved.id != null) {
                await markChatRoomRead(roomId, saved.id);
            }
        }
        catch (e) {
            console.error('[CRM Chat] send failed', e);
            throw e;
        }
    }, [notifyTyping]);
    useEffect(() => {
        if (!enabled) {
            setConnectionState('idle');
            return undefined;
        }
        acquireCrmStompSharedConnection(sockJsUrl, { debug });
        const detachState = onCrmStompSharedConnectionState((state) => {
            if (state === 'connecting')
                setConnectionState('connecting');
            else if (state === 'connected')
                setConnectionState('connected');
            else if (state === 'disconnected') {
                needsHistoryCatchUpRef.current = true;
                setConnectionState('disconnected');
            }
            else if (state === 'error')
                setConnectionState('error');
            else
                setConnectionState('idle');
        });
        const handleConnect = () => {
            clientRef.current = getCrmStompSharedClient();
            tearDownSubscriptions();
            subscribeUserTopicRef.current();
            syncTopicSubscriptionsRef.current(roomIdsRef.current);
            const active = currentRoomIdRef.current;
            if (active != null) {
                subscribeRoomRef.current(active);
                if (needsHistoryCatchUpRef.current) {
                    needsHistoryCatchUpRef.current = false;
                    void loadRoomHistoryRef.current(active);
                }
            }
        };
        const detachConnect = onCrmStompSharedConnect(handleConnect);
        clientRef.current = getCrmStompSharedClient();
        return () => {
            detachConnect();
            detachState();
            if (markReadTimerRef.current != null) {
                window.clearTimeout(markReadTimerRef.current);
                markReadTimerRef.current = null;
            }
            if (typingStopTimerRef.current != null) {
                window.clearTimeout(typingStopTimerRef.current);
                typingStopTimerRef.current = null;
            }
            for (const timerId of typingInboundExpiryRef.current.values()) {
                window.clearTimeout(timerId);
            }
            typingInboundExpiryRef.current.clear();
            tearDownSubscriptions();
            clientRef.current = null;
            currentRoomIdRef.current = null;
            setCurrentRoomId(null);
            releaseCrmStompSharedConnection();
            setConnectionState('idle');
        };
    }, [debug, enabled, sockJsUrl, tearDownSubscriptions]);
    useEffect(() => {
        if (connectionState === 'connected') {
            syncTopicSubscriptionsRef.current(roomIds);
        }
    }, [connectionState, roomIds]);
    useEffect(() => {
        if (connectionState === 'connected') {
            subscribeUserTopicRef.current();
        }
    }, [connectionState, username]);
    return useMemo(() => ({
        connectionState,
        roomIds,
        currentRoomId,
        entriesByRoom: ui.entriesByRoom,
        unreadByRoom: ui.unreadByRoom,
        typingUsers,
        selectRoom,
        syncRooms,
        sendMessage,
        notifyTyping,
        appendSystemLine,
    }), [
        appendSystemLine,
        connectionState,
        currentRoomId,
        notifyTyping,
        roomIds,
        selectRoom,
        sendMessage,
        syncRooms,
        typingUsers,
        ui.entriesByRoom,
        ui.unreadByRoom,
    ]);
}
